import { getV1SessionCapabilities } from "@solarkey/api-client";

import { STUB_CAPABILITIES } from "./capabilities.stub";
import type { SessionCapabilities } from "./types";

/**
 * GET /v1/session/capabilities — bootstrapped once per session (SessionGate).
 *
 * The server resolves the person's lenses, authority and rail families from their
 * role and the tenant tier (UX-001 capability resolver) and returns them in the
 * {data, meta, capabilities} envelope. We read `data` and map it to the shell's
 * SessionCapabilities shape: `scope_roots` ARE the `scopes` (same {id,label,type}),
 * and the tenant node's id is the slug the scoped routes use.
 *
 * The UI renders from this; it never decides access (UX-005 §2.1 / UX-001 #8).
 */
export async function loadCapabilities(): Promise<SessionCapabilities> {
  try {
    const res = await getV1SessionCapabilities();
    if (res.status !== 200) throw new Error(`capabilities ${res.status}`);
    return toCapabilities(res.data.data);
  } catch (err) {
    // In dev without a backend, fall back to the stub so the shell is still usable.
    // In prod a failure is surfaced (SessionGate shows the error state) — never faked.
    if (import.meta.env.DEV) return STUB_CAPABILITIES;
    throw err;
  }
}

/** Map the envelope `data` to the shell contract. Missing optionals degrade to the
 *  safe empty/closed value (no lens, no family) — never an invented capability. */
function toCapabilities(d: EnvelopeData): SessionCapabilities {
  const raw = d as Record<string, unknown>;
  return {
    // `role` is an additive field on person (the server returns it); the frozen
    // openapi person type predates it, so read it through a narrow cast.
    role: (d.person as { role?: string | null } | undefined)?.role ?? null,
    tier: (typeof raw.tier === "string" ? raw.tier : "easy_m") as SessionCapabilities["tier"],
    scopes: (d.scope_roots ?? []).map((s) => ({
      id: s.id ?? "",
      label: s.label ?? "",
      type: s.type ?? "",
    })),
    accessible_tenants: (d.accessible_tenants ?? []).map((t) => ({
      id: t.id ?? "",
      slug: t.slug ?? "",
      name: t.name ?? "",
    })),
    grouping_dimensions: d.grouping_dimensions ?? [],
    lenses: (d.lenses ?? []) as SessionCapabilities["lenses"],
    authority: (d.authority ?? {
      limits: [],
      reach: "string",
      autonomy: "observe",
      four_eyes: false,
      can_grant: false,
    }) as SessionCapabilities["authority"],
    entitlements: (d.entitlements ?? {}) as Record<string, boolean>,
    rail_families: (d.rail_families ?? []).map((f) => {
      const rf = f as Record<string, unknown>;
      const locked = f.locked ?? false;
      return {
        name: f.name ?? "",
        visible: f.visible ?? false,
        locked,
        locked_tier: typeof rf.locked_tier === "string" ? rf.locked_tier : locked ? "Easy T" : undefined,
        stub: typeof rf.stub === "boolean" ? rf.stub : undefined,
      };
    }),
    locale: safeLocale(d.locale),
    currency: d.currency || "",
    financial_year_start: d.financial_year_start || "01-01",
    theme: (d.theme ?? "system") as SessionCapabilities["theme"],
    density: (d.density ?? "comfortable") as SessionCapabilities["density"],
  };
}

/** A valid BCP-47 locale, or "en". Guards the Intl layer against a malformed
 *  tenant locale (and the contract mocks' random-string locale) that would
 *  otherwise throw inside IntlProvider and blank the whole shell. */
function safeLocale(l?: string): string {
  if (!l) return "en";
  try {
    return Intl.getCanonicalLocales(l).length > 0 ? l : "en";
  } catch {
    return "en";
  }
}

type EnvelopeData = Awaited<
  ReturnType<typeof getV1SessionCapabilities>
>["data"]["data"];
